import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getSettings, editSettings, getLunch, editLunch, addLunch, deleteLunch, getRoles, saveChangedRole, getEmployeeStatisticsSettings, editEmployeeStatisticsSettings } from '@/api/settings'
import { useToast } from 'vue-toast-notification'
import type { EmployeeStatisticsSettings, EmployeeStatisticsSettingsPatch } from '@/types'

const toast = useToast({ position: 'top' })

export const useSettingsStore = defineStore('settings', () => {
  // Состояния
  const loading = ref(false)
  const settings = ref<any>()
  const employeeStatisticsSettings = ref<EmployeeStatisticsSettings | null>(null)
  const employeeStatisticsSettingsLoading = ref(false)
  const lunchSettings = ref<any[]>([])
  const roles = ref<any[]>([])

  // Функции 
  async function fetchSettings() {
    loading.value = true
    try {
      const res = await getSettings()
      settings.value = res.data.body
      loading.value = false
    } catch (err: any) {
      loading.value = false
      return err
    }
  }

  async function changeSettings(data: any) {
    try {
      const res = await editSettings(data)
      settings.value = res.data.body
    } catch (err: any) {
      return err
    }
  }

  async function fetchEmployeeStatisticsSettings() {
    employeeStatisticsSettingsLoading.value = true
    try {
      const res = await getEmployeeStatisticsSettings()
      employeeStatisticsSettings.value = res.data.body
    } catch (err: any) {
      return err
    } finally {
      employeeStatisticsSettingsLoading.value = false
    }
  }

  async function changeEmployeeStatisticsSettings(data: EmployeeStatisticsSettingsPatch) {
    try {
      const res = await editEmployeeStatisticsSettings(data)
      employeeStatisticsSettings.value = res.data.body
    } catch (err: any) {
      return err
    }
  }

  async function fetchLunchSettings() {
    loading.value = true
    try {
      const res = await getLunch()
      if (res.data) {
        lunchSettings.value = res.data.body
        sortLunches()
      }
      loading.value = false
    } catch (err: any) {
      loading.value = false
      return err
    }
  }

  async function createLunch(data: any) {
    try {
      const res = await addLunch(data)
      if (res.data) {
        lunchSettings.value.push(res.data.body)
        toast.success(res.data.msg)
        sortLunches()
      }
    } catch (err: any) {
      return err
    }
  }

  async function editLunchSettings(id: string, data: any) {
    loading.value = true
    try {
      const res = await editLunch(id, data)
      if (res.data) {
        const lunch = res.data.body
        const index = lunchSettings.value.findIndex(item => item.id === lunch.id);

        if (index !== -1) {
          lunchSettings.value.splice(index, 1, lunch);
        }
        toast.success(res.data.msg)
      }
      sortLunches()
      loading.value = false
    } catch (err: any) {
      loading.value = false
      return err
    }
  }

  async function removeLunch(id: string) {
    try {
      lunchSettings.value = lunchSettings.value.filter(lunch => lunch.id !== id)
      await deleteLunch(id)
      toast.success('Удалено успешно')
    } catch (err: any) {
      return err
    }
  }

  function sortLunches() {
    lunchSettings.value.sort((a,b) => a.shift_duration_seconds - b.shift_duration_seconds)
  }

  async function fetchRoles() {
    try {
      const res = await getRoles()
      if (res.data) {
        roles.value = res.data.body
      }
    } catch (err: any) {
      return err
    }
  }

  async function changeRole(id:string|number, data:any) {
    try {
      const res = await saveChangedRole(id, data)
      if (res.data) {
        toast.success(res.data.msg)
        replaceObjectById(roles.value, id, res.data.body)
      }
    } catch (err: any) {
      return err
    }
  }

  function replaceObjectById(array:any[], id:any, newObject:any) {
  const index = array.findIndex(item => item.id === id);
  if (index !== -1) {
    array[index] = newObject;
    return true; // Успех
  }
  return false; // Объект с таким id не найден
}

  return {
    // Состояния
    loading,
    settings,
    employeeStatisticsSettings,
    employeeStatisticsSettingsLoading,
    lunchSettings,
    roles,
    // Функции
    fetchSettings,
    changeSettings,
    fetchEmployeeStatisticsSettings,
    changeEmployeeStatisticsSettings,
    fetchLunchSettings,
    editLunchSettings,
    createLunch,
    removeLunch,
    fetchRoles,
    changeRole
  }
})
